import { Component, OnDestroy } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { UserTokenService } from '@dep/frontend/users/services/user-token.service';
import { UserService } from '@dep/frontend/users/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-curation-panel',
  templateUrl: './curation-panel.component.html',
  styleUrls: ['./curation-panel.component.scss'],
  standalone: false,
})
export class CurationPanelComponent implements OnDestroy {
  public automatedContentUrl = environment.config.automatedContent.url;
  private accessToken: Awaited<ReturnType<typeof this.userService.getAccessToken>> | null = null;
  private idToken: string | null = null;
  private timeout?: number;

  constructor(
    private userService: UserService,
    private userTokenService: UserTokenService,
    private logger: NGXLogger,
  ) { }

  public ngOnDestroy(): void {
    this.accessToken = null;
    this.idToken = null;
    clearTimeout(this.timeout);
  }

  private async nextSend(win: Window): Promise<void> {
    await this.sendToken(win);

    if (!this.idToken) {
      this.logger.error('ID token not set', this.idToken);
      throw new Error('ID token not set');
    }

    // Refresh the token when it expires.
    // Therefore, subtract the current timestamp from the expiration timestamp to get the remaining milliseconds.
    const nextSendDelayInMs = (this.userTokenService.getTokenExpiration(this.idToken) * 1000) - Date.now();
    this.logger.debug(`Scheduling next token sending to curation panel in ${nextSendDelayInMs} ms`);

    this.timeout = window.setTimeout(
      () => {
        void this.nextSend(win);
      },
      nextSendDelayInMs,
    );
  }

  public passAuth(): void {
    this.logger.debug('Passing authentication to curation panel iframe');

    const iframe = document.getElementById('curation-panel');
    if (iframe === null) {
      return;
    }
    const iWindow = (<HTMLIFrameElement>iframe).contentWindow;
    if (iWindow) {
      void this.nextSend(iWindow);
    }
    iframe.style.height = '2000px';
  }

  private async sendToken(win: Window): Promise<void> {
    this.logger.debug('Getting tokens to be sent to curation panel');

    this.accessToken = await this.userService.getAccessToken();

    // Only fetch the ID token if none is set yet. Refreshing the token is done by the `timeout` and does not need to be handled here.
    if (!this.idToken) {
      // Get specialized JWT for automated content service with "AUTOMATEDCONTENT_" roles.
      const headers = await this.userTokenService.getAuthorizationHeadersForService('automated-content');
      this.idToken = headers.get('IdToken');
    }

    this.logger.debug('Sending Automated Content JWTs to the curation panel iframe');

    win.postMessage(
      {
        accessToken: this.accessToken,
        idToken: this.idToken,
      },
      this.automatedContentUrl,
    );
  }
}
